// Marquee unidirecional com loop seamless. Quando o conteúdo do .marquee-inner
// transborda o .marquee-outer, JS duplica o filho e seta CSS vars pra que a
// animação saia da posição 0 e termine quando a cópia ocupa a posição inicial
// — efeito seamless. Quando não transborda, não anima.
//
// CSS associado em apps/ui/src/styles/globals.css (.marquee-outer, .marquee-inner,
// .marquee-copy, @keyframes marquee-loop).

import { useEffect, useRef } from "preact/hooks";

export function MarqueeOnHover(props: {
  children: any;
  class?: string;
  className?: string;
}) {
  const className = props.class ?? props.className ?? "";
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);
  const firstCopyRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    const first = firstCopyRef.current;
    if (!outer || !inner || !first) return;
    function check() {
      const copyW = first!.offsetWidth;
      const outerW = outer!.clientWidth;
      // Quando o elemento está oculto (display:none, modal fechado), as
      // dimensões vêm como 0. Não trate isso como "no overflow" — apenas
      // limpe o estado e aguarde IntersectionObserver / ResizeObserver
      // re-disparar quando virar visível.
      if (copyW === 0 || outerW === 0) return;
      const overflows = copyW > outerW + 2;
      outer!.dataset.overflow = overflows ? "true" : "false";
      if (overflows) {
        outer!.style.setProperty("--marquee-delta", `-${copyW}px`);
        const dur = Math.max(6, copyW / 50);
        outer!.style.setProperty("--marquee-duration", `${dur}s`);
      } else {
        outer!.style.removeProperty("--marquee-delta");
        outer!.style.removeProperty("--marquee-duration");
      }
    }
    check();
    // 1ª chance no próximo paint (cobre layout pós-fonte)
    const raf = requestAnimationFrame(check);
    // Retries timeados pra cobrir animações de entrada (modal pop-in etc)
    const t1 = setTimeout(check, 250);
    const t2 = setTimeout(check, 700);
    const t3 = setTimeout(check, 1500);
    // ResizeObserver pega mudanças de tamanho
    const ro = new ResizeObserver(check);
    ro.observe(outer);
    ro.observe(inner);
    // IntersectionObserver pega quando o elemento entra na viewport (scroll,
    // modal abrir, sidebar expandir etc) — re-mede com dimensões reais.
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) check();
      }
    });
    io.observe(outer);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      ro.disconnect();
      io.disconnect();
    };
  }, [props.children]);

  return (
    <div
      ref={outerRef}
      class={`marquee-outer relative ${className}`}
      data-overflow="false"
    >
      <span ref={innerRef} class="marquee-inner">
        <span ref={firstCopyRef} class="marquee-copy">
          {props.children}
        </span>
        <span class="marquee-copy" aria-hidden="true">
          {props.children}
        </span>
      </span>
    </div>
  );
}
